// src/pages/curriculum/ReadOnlyGrids.tsx

import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { AlertTriangle, Eye, Grid as GridIcon, RefreshCw, Filter, X, Lock, ShieldAlert } from 'lucide-react';
import PageHeader from '../../components/layout/PageHeader';
import Button from '../../components/ui/Button';
import Card from '../../components/ui/Card';
import { useToast } from '../../contexts/ToastContext';
import { useAuth } from '../../contexts/AuthContext';
import { useData } from '../../contexts/DataContext';
import { gridsService } from '../../services/gridsService';
import ReadOnlyGridViewer from '../../components/grid/ReadOnlyGridViewer';
import Spinner from '../../components/ui/Spinner';
import { DEFAULT_MINUTES_PER_LINE } from '../../types/schoolConfig';
import { displayLearningArea } from '../../constants/learningAreas';
import { filterClassesForLeaderArea } from '../../utils/curriculumAreaScope';

const ReadOnlyGrids: React.FC = () => {
  const { addToast } = useToast();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { staff } = useData();

  const [grids, setGrids] = useState<any[]>([]);
  const [selectedGrid, setSelectedGrid] = useState<any | null>(null);
  const [loading, setLoading] = useState(true);
  const [showGridModal, setShowGridModal] = useState(false);

  // Filter states
  const [semesterFilter, setSemesterFilter] = useState<'all' | 'semester1' | 'semester2' | 'fullyear'>('all');
  const [teacherFilter, setTeacherFilter] = useState<string>('all');
  const [classFilter, setClassFilter] = useState<string>('');
  const [curriculumAreaFilter, _setCurriculumAreaFilter] = useState<string>('all');

  // Check if user has access
  const hasAccess = user?.role === 'admin' || user?.role === 'curriculum_leader' || user?.role === 'assistant_principal' || user?.role === 'head_of_department';
  const isCurriculumLeader = user?.role === 'curriculum_leader';
  const isAssistantPrincipal = user?.role === 'assistant_principal';
  const isHeadOfDepartment = user?.role === 'head_of_department';
  const userCurriculumArea = user?.curriculumArea || '';

  // Protected setter for curriculum area filter - prevents curriculum leaders from changing it
  const setCurriculumAreaFilter = (value: string) => {
    // Curriculum leaders cannot change their curriculum area filter
    if (isCurriculumLeader) {
      console.warn('Curriculum leaders cannot change curriculum area filter');
      return;
    }
    _setCurriculumAreaFilter(value);
  };

  useEffect(() => {
    if (hasAccess) {
      loadGrids();
    }
  }, [hasAccess]);

  const loadGrids = async () => {
    try {
      setLoading(true);
      const gridsData = await gridsService.getAllGrids();
      setGrids(gridsData || []);
    } catch (error) {
      console.error('Error loading grids:', error);
      addToast('Failed to load grids', 'error');
    } finally {
      setLoading(false);
    }
  };

  // Which classes a curriculum leader may see. The matching lives in
  // curriculumAreaScope so this page, Teacher Assignments and the grid viewer
  // all answer the question the same way (issue #187).
  const filterClassesByCurriculumArea = (classes: any[], curriculumArea: string, gridName?: string) => {
    if (!curriculumArea || !classes) return classes;
    return filterClassesForLeaderArea(classes, curriculumArea, gridName);
  };

  // Filter selected grid to only include curriculum leader's classes
  // This ensures they cannot see other curriculum areas even via dev tools
  const filteredSelectedGrid = useMemo(() => {
    if (!selectedGrid) return null;

    // Only filter for curriculum leaders (NOT for head of department)
    if (!isCurriculumLeader || !userCurriculumArea) {
      return selectedGrid;
    }

    // Return a new grid object with filtered classes
    return {
      ...selectedGrid,
      classes: filterClassesByCurriculumArea(selectedGrid.classes, userCurriculumArea, selectedGrid.name)
    };
  }, [selectedGrid, isCurriculumLeader, userCurriculumArea]);

  // Get unique curriculum areas from FILTERED grid
  const curriculumAreas = useMemo(() => {
    if (!filteredSelectedGrid?.classes) return [];
    const areas = new Set<string>();
    filteredSelectedGrid.classes.forEach((c: any) => {
      if (c.curriculumArea) areas.add(c.curriculumArea);
    });
    return Array.from(areas).sort();
  }, [filteredSelectedGrid]);

  // Get unique class names from FILTERED grid
  const classNames = useMemo(() => {
    if (!filteredSelectedGrid?.classes) return [];
    const names = new Set<string>();
    filteredSelectedGrid.classes.forEach((c: any) => {
      if (c.className || c.subjectName) {
        names.add(c.className || c.subjectName);
      }
    });
    return Array.from(names).sort();
  }, [filteredSelectedGrid]);

  // Get unique teacher codes from FILTERED grid
  const teacherCodes = useMemo(() => {
    if (!filteredSelectedGrid?.classes) return [];
    const codes = new Set<string>();
    filteredSelectedGrid.classes.forEach((c: any) => {
      if (c.lineAssignments) {
        Object.values(c.lineAssignments).forEach((assignment: any) => {
          // Handle different assignment formats
          if (Array.isArray(assignment)) {
            assignment.forEach((a: any) => {
              if (a.teacherCode) codes.add(a.teacherCode);
            });
          } else if (typeof assignment === 'string') {
            // Try parsing JSON string first
            if (assignment.trim().startsWith('[') || assignment.trim().startsWith('{')) {
              try {
                const parsed = JSON.parse(assignment);
                if (Array.isArray(parsed)) {
                  parsed.forEach((a: any) => {
                    if (a.teacherCode) codes.add(a.teacherCode);
                  });
                } else if (parsed.teacherCode) {
                  codes.add(parsed.teacherCode);
                }
              } catch (e) {
                // Not valid JSON, treat as plain teacher code
                codes.add(assignment);
              }
            } else {
              codes.add(assignment);
            }
          } else if (typeof assignment === 'object' && assignment.teacherCode) {
            codes.add(assignment.teacherCode);
          }
        });
      }
    });
    return Array.from(codes).sort();
  }, [filteredSelectedGrid]);

  // Filter classes in grid by curriculum area
  const getFilteredClassCount = (grid: any) => {
    if (!grid.classes) return 0;

    // Assistant principals and heads of department see all curriculum areas (no filtering)
    if (isAssistantPrincipal || isHeadOfDepartment) {
      return grid.classes.length;
    }

    // Curriculum leaders see only their curriculum area
    if (isCurriculumLeader && userCurriculumArea) {
      // Use the same filtering logic as filterClassesByCurriculumArea
      return filterClassesByCurriculumArea(grid.classes, userCurriculumArea, grid.name).length;
    }

    // Admins and others see all
    return grid.classes.length;
  };

  // Reset filters when grid changes
  useEffect(() => {
    if (showGridModal && selectedGrid) {
      setSemesterFilter('all');
      setTeacherFilter('all');
      setClassFilter('');
      // Curriculum leaders auto-filter to their area, assistant principals see all
      // Use internal setter to bypass protection for initialization
      _setCurriculumAreaFilter(isCurriculumLeader ? userCurriculumArea : 'all');
    }
  }, [showGridModal, selectedGrid, isCurriculumLeader, userCurriculumArea]);

  // Early return for no access
  if (!hasAccess) {
    return (
      <div>
        <PageHeader
          title="Access Denied"
          description="You don't have permission to view grid assignments"
        />
        <Card>
          <div className="p-12 text-center">
            <AlertTriangle className="h-12 w-12 text-danger-fg mx-auto mb-4" />
            <h3 className="text-card-title text-ink-900 mb-2">Insufficient Permissions</h3>
            <p className="text-sm text-ink-500 mb-6">
              This page is restricted to Curriculum Leaders and Administrators only.
            </p>
            <Button variant="primary" onClick={() => navigate('/dashboard')}>
              Return to Dashboard
            </Button>
          </div>
        </Card>
      </div>
    );
  }

  return (
    <div>
      <PageHeader
        title={isCurriculumLeader ? `${userCurriculumArea} Grid Assignments` : 'Grid Assignments'}
        description={
          isCurriculumLeader
            ? `View actual grid assignments for ${userCurriculumArea} classes (Read-Only)`
            : isHeadOfDepartment
              ? 'View actual grid assignments for all learning areas (Read-Only)'
              : 'View actual grid assignments for all classes (Read-Only)'
        }
        actions={
          <Button
            variant="secondary"
            leftIcon={<RefreshCw className="h-4 w-4" />}
            onClick={loadGrids}
            disabled={loading}
          >
            Refresh
          </Button>
        }
      />

      {/* CONFIDENTIALITY WARNING BANNER */}
      <Card className="mb-6 border-2 border-danger-border">
        <div className="p-5 bg-danger-bg">
          <div className="flex items-start">
            <ShieldAlert className="h-8 w-8 text-danger-fg mr-4 mt-0.5 flex-shrink-0" />
            <div className="flex-1">
              <h3 className="text-card-title text-danger-fg mb-2 flex items-center">
                <Lock className="h-5 w-5 mr-2" />
                CONFIDENTIAL INFORMATION
              </h3>
              <p className="text-sm font-semibold text-danger-fg mb-2">
                The teacher assignments shown on this page are CONFIDENTIAL and must not be shared with staff members until officially released by school leadership.
              </p>
              <p className="text-sm text-danger-fg">
                This information is provided to curriculum leaders for planning purposes only. Unauthorised disclosure may breach school policy and professional confidentiality.
              </p>
            </div>
          </div>
        </div>
      </Card>

      {/* Info Card */}
      <Card className="mb-6">
        <div className="p-4 bg-navy-50 border-l-4 border-navy-500">
          <div className="flex items-start">
            <Eye className="h-5 w-5 text-navy-600 mr-3 mt-0.5 flex-shrink-0" />
            <div>
              <h3 className="text-sm font-medium text-navy-900 mb-1">
                Read-Only Grid View
              </h3>
              <p className="text-sm text-navy-800">
                This page shows the <strong>actual assignments</strong> in each grid.
                {isCurriculumLeader && (
                  <span> You can see assignments for <strong>{userCurriculumArea}</strong> classes only.</span>
                )}
                {' '}To manage your <strong>preferences</strong> (who you'd like to teach),
                visit <strong>Curriculum Areas → Teacher Assignments</strong>.
              </p>
            </div>
          </div>
        </div>
      </Card>

      {loading ? (
        <Card>
          <div className="p-12 text-center">
            <Spinner size="lg" label="Loading grids…" className="justify-center" />
          </div>
        </Card>
      ) : grids.length === 0 ? (
        <Card>
          <div className="p-12 text-center">
            <GridIcon className="h-12 w-12 text-ink-400 mx-auto mb-4" />
            <h3 className="text-card-title text-ink-900 mb-2">No Grids Available</h3>
            <p className="text-sm text-ink-500 mb-6">
              There are no grids to display. Grids are created by administrators.
            </p>
          </div>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {grids.map((grid) => {
            const classCount = getFilteredClassCount(grid);
            const lineCount = grid.lines?.length || 0;
            const hasClasses = classCount > 0;

            return (
              <Card key={grid.id}>
                <div className="p-6">
                  <div className="flex items-start justify-between mb-4">
                    <div className="flex-1">
                      <h3 className="text-card-title text-ink-900 mb-1">
                        {grid.name}
                      </h3>
                      {grid.year && (
                        <p className="font-mono tabular-nums text-sm text-ink-500">Year: {grid.year}</p>
                      )}
                    </div>
                    <GridIcon className="h-8 w-8 text-navy-600" />
                  </div>

                  <div className="space-y-3 mb-4">
                    <div className="flex justify-between text-sm">
                      <span className="text-ink-500">Classes:</span>
                      <span className="font-mono tabular-nums font-medium text-ink-900">{classCount}</span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span className="text-ink-500">Lines:</span>
                      <span className="font-mono tabular-nums font-medium text-ink-900">{lineCount}</span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span className="text-ink-500">Minutes/Line:</span>
                      <span className="font-mono tabular-nums font-medium text-ink-900">
                        {grid.defaultClassMinutes || DEFAULT_MINUTES_PER_LINE}
                      </span>
                    </div>
                  </div>

                  {hasClasses ? (
                    <Button
                      variant="primary"
                      className="w-full"
                      leftIcon={<Eye className="h-4 w-4" />}
                      onClick={() => {
                        setSelectedGrid(grid);
                        setShowGridModal(true);
                      }}
                    >
                      View Grid
                    </Button>
                  ) : (
                    <div className="text-center py-2">
                      <p className="text-sm text-ink-500 italic">
                        No {isCurriculumLeader ? userCurriculumArea : ''} classes
                      </p>
                    </div>
                  )}
                </div>
              </Card>
            );
          })}
        </div>
      )}

      {/* Grid Viewer Modal - Full Screen */}
      {showGridModal && selectedGrid && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-2">
          <div className="bg-white rounded-lg shadow-2xl w-[98vw] h-[98vh] flex flex-col">
            {/* Header */}
            <div className="px-6 py-4 border-b flex justify-between items-center flex-shrink-0">
              <div>
                <h3 className="text-section text-ink-900">
                  {selectedGrid.name}
                  {isCurriculumLeader && (
                    <span className="ml-2 text-sm font-normal text-navy-600">
                      ({userCurriculumArea} only)
                    </span>
                  )}
                </h3>
                <p className="text-sm text-ink-500 mt-1">
                  Read-only view • {filteredSelectedGrid?.classes?.length || 0} classes total
                </p>
              </div>
              <button
                className="text-ink-400 hover:text-ink-500 text-3xl leading-none"
                onClick={() => {
                  setShowGridModal(false);
                  setSelectedGrid(null);
                }}
              >
                ×
              </button>
            </div>

            {/* Filters Bar */}
            <div className="px-6 py-4 bg-ink-50 border-b flex-shrink-0">
              <div className="flex items-center gap-4 flex-wrap">
                <div className="flex items-center gap-2">
                  <Filter className="h-4 w-4 text-ink-500" />
                  <span className="text-sm font-medium text-ink-700">Filters:</span>
                </div>

                {/* Semester Filter */}
                <div className="flex items-center gap-2">
                  <label className="text-sm text-ink-500">Semester:</label>
                  <select
                    value={semesterFilter}
                    onChange={(e) => setSemesterFilter(e.target.value as any)}
                    className="px-3 py-1.5 text-sm border border-ink-300 rounded-md focus:outline-none focus:ring-2 focus:ring-navy-500"
                  >
                    <option value="all">All</option>
                    <option value="semester1">Semester 1</option>
                    <option value="semester2">Semester 2</option>
                    <option value="fullyear">Full Year</option>
                  </select>
                </div>

                {/* Curriculum Area Filter - Only shown for admins, assistant principals, and heads of department */}
                {!isCurriculumLeader && (
                  <div className="flex items-center gap-2">
                    <label className="text-sm text-ink-500">Learning Area:</label>
                    <select
                      value={curriculumAreaFilter}
                      onChange={(e) => setCurriculumAreaFilter(e.target.value)}
                      className="px-3 py-1.5 text-sm border border-ink-300 rounded-md focus:outline-none focus:ring-2 focus:ring-navy-500"
                    >
                      <option value="all">All Learning Areas</option>
                      {curriculumAreas.map(area => (
                        <option key={area} value={area}>{displayLearningArea(area)}</option>
                      ))}
                    </select>
                  </div>
                )}
                {/* Show locked curriculum area for curriculum leaders */}
                {isCurriculumLeader && (
                  <div className="flex items-center gap-2 px-3 py-1.5 bg-navy-50 border border-navy-200 rounded-md">
                    <label className="text-sm text-ink-500">Learning Area:</label>
                    <span className="text-sm font-medium text-navy-700">{userCurriculumArea}</span>
                    <span className="text-xs text-navy-600">(locked)</span>
                  </div>
                )}

                {/* Teacher Filter */}
                <div className="flex items-center gap-2">
                  <label className="text-sm text-ink-500">Teacher:</label>
                  <select
                    value={teacherFilter}
                    onChange={(e) => setTeacherFilter(e.target.value)}
                    className="px-3 py-1.5 text-sm border border-ink-300 rounded-md focus:outline-none focus:ring-2 focus:ring-navy-500"
                  >
                    <option value="all">All Teachers</option>
                    {teacherCodes.map(code => {
                      const teacherName = staff?.find(s => s.initial === code || s.code === code)?.name || code;
                      return (
                        <option key={code} value={code}>{code} - {teacherName}</option>
                      );
                    })}
                  </select>
                </div>

                {/* Class Search */}
                <div className="flex items-center gap-2">
                  <label className="text-sm text-ink-500">Class:</label>
                  <input
                    type="text"
                    value={classFilter}
                    onChange={(e) => setClassFilter(e.target.value)}
                    placeholder="Search class..."
                    className="px-3 py-1.5 text-sm border border-ink-300 rounded-md focus:outline-none focus:ring-2 focus:ring-navy-500 w-48"
                  />
                  {classFilter && (
                    <button
                      onClick={() => setClassFilter('')}
                      className="text-ink-400 hover:text-ink-500"
                    >
                      <X className="h-4 w-4" />
                    </button>
                  )}
                </div>

                {/* Clear All Filters */}
                {(semesterFilter !== 'all' || teacherFilter !== 'all' || classFilter !== '' || (curriculumAreaFilter !== 'all' && !isCurriculumLeader)) && (
                  <button
                    onClick={() => {
                      setSemesterFilter('all');
                      setTeacherFilter('all');
                      setClassFilter('');
                      if (!isCurriculumLeader) setCurriculumAreaFilter('all');
                    }}
                    className="ml-auto px-3 py-1.5 text-sm text-navy-600 hover:text-navy-800 font-medium"
                  >
                    Clear Filters
                  </button>
                )}
              </div>
            </div>

            {/* Grid Content */}
            <div className="flex-1 overflow-auto p-6">
              <ReadOnlyGridViewer
                grid={filteredSelectedGrid}
                curriculumAreaFilter={isCurriculumLeader ? undefined : (curriculumAreaFilter !== 'all' ? curriculumAreaFilter : undefined)}
                semesterFilter={semesterFilter}
                teacherFilter={teacherFilter !== 'all' ? teacherFilter : undefined}
                classFilter={classFilter}
                staff={staff}
              />
            </div>

            {/* Footer */}
            <div className="px-6 py-4 border-t flex justify-between items-center flex-shrink-0 bg-ink-50">
              <div className="text-sm text-ink-500">
                <strong>Note:</strong> This is a read-only view. Contact an administrator to make changes.
              </div>
              <Button
                variant="secondary"
                onClick={() => {
                  setShowGridModal(false);
                  setSelectedGrid(null);
                }}
              >
                Close
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ReadOnlyGrids;
